import { Reader, stringAt } from "./reader.ts";

const DW_LNS_copy = 1;
const DW_LNS_advance_pc = 2;
const DW_LNS_advance_line = 3;
const DW_LNS_set_file = 4;
const DW_LNS_const_add_pc = 8;
const DW_LNS_fixed_advance_pc = 9;
const DW_LNE_end_sequence = 1;
const DW_LNE_set_address = 2;
const DW_LNE_define_file = 3;

const DW_LNCT_path = 1;
const DW_LNCT_directory_index = 2;

/** One row of a line table: an address and the source line it starts. */
interface Row {
    address: number;
    file: number;
    line: number;
}

interface Sequence {
    start: number;
    end: number;
    rows: Row[];
}

export interface LineSections {
    line: Uint8Array;
    str?: Uint8Array;
    lineStr?: Uint8Array;
}

/**
 * One compilation unit's line number program, run to completion: file names
 * made into full paths, and rows grouped into address-ordered sequences.
 */
export class LineTable {
    readonly #files: string[];
    readonly #sequences: Sequence[];

    constructor(sections: LineSections, offset: number, compDir: string) {
        const r = new Reader(sections.line, offset);
        const unitLength = r.u32();
        if (unitLength === 0xffffffff) throw new Error("64-bit DWARF is not supported");
        const end = r.offset + unitLength;
        const version = r.u16();
        let addressSize = 4;
        if (version >= 5) {
            addressSize = r.u8();
            r.u8();
        }
        const headerLength = r.u32();
        const programStart = r.offset + headerLength;
        const minInstLength = r.u8();
        if (version >= 4) r.u8();
        r.u8();
        const lineBase = r.i8();
        const lineRange = r.u8();
        const opcodeBase = r.u8();
        const opcodeLengths: number[] = [];
        for (let i = 1; i < opcodeBase; i++) opcodeLengths.push(r.u8());

        const directories: string[] = [];
        const files: string[] = [];
        if (version >= 5) {
            for (const entry of readEntries(r, sections, addressSize)) {
                directories.push(entry.path);
            }
            for (const entry of readEntries(r, sections, addressSize)) {
                files.push(join(join(compDir, directories[entry.directory] ?? ""), entry.path));
            }
        } else {
            directories.push(compDir);
            for (let dir = r.cstr(); dir !== ""; dir = r.cstr()) directories.push(join(compDir, dir));
            // DWARF 4 counts files from 1.
            files.push("");
            for (let name = r.cstr(); name !== ""; name = r.cstr()) {
                files.push(join(directories[r.uleb()] ?? compDir, name));
                r.uleb();
                r.uleb();
            }
        }
        this.#files = files;

        r.offset = programStart;
        const sequences: Sequence[] = [];
        let rows: Row[] = [];
        let address = 0;
        let file = 1;
        let line = 1;
        const emit = () => rows.push({ address, file, line });
        while (r.offset < end) {
            const opcode = r.u8();
            if (opcode >= opcodeBase) {
                const adjusted = opcode - opcodeBase;
                address += Math.floor(adjusted / lineRange) * minInstLength;
                line += lineBase + (adjusted % lineRange);
                emit();
            } else if (opcode === 0) {
                const length = r.uleb();
                const next = r.offset + length;
                const sub = r.u8();
                if (sub === DW_LNE_end_sequence) {
                    if (rows.length > 0) sequences.push({ start: rows[0]!.address, end: address, rows });
                    rows = [];
                    address = 0;
                    file = 1;
                    line = 1;
                } else if (sub === DW_LNE_set_address) {
                    address = r.sized(length - 1);
                } else if (sub === DW_LNE_define_file) {
                    const name = r.cstr();
                    files.push(join(directories[r.uleb()] ?? compDir, name));
                }
                r.offset = next;
            } else if (opcode === DW_LNS_copy) {
                emit();
            } else if (opcode === DW_LNS_advance_pc) {
                address += r.uleb() * minInstLength;
            } else if (opcode === DW_LNS_advance_line) {
                line += r.sleb();
            } else if (opcode === DW_LNS_set_file) {
                file = r.uleb();
            } else if (opcode === DW_LNS_const_add_pc) {
                address += Math.floor((255 - opcodeBase) / lineRange) * minInstLength;
            } else if (opcode === DW_LNS_fixed_advance_pc) {
                address += r.u16();
            } else {
                for (let i = 0; i < opcodeLengths[opcode - 1]!; i++) r.uleb();
            }
        }
        this.#sequences = sequences.sort((a, b) => a.start - b.start);
    }

    /** File identified by index, as referenced by DW_AT_call_file and line rows. */
    file(index: number): string | undefined {
        return this.#files[index];
    }

    /** The source line an address comes from. */
    find(address: number): { file: string | undefined; line: number } | undefined {
        for (const sequence of this.#sequences) {
            if (address < sequence.start || address >= sequence.end) continue;
            const rows = sequence.rows;
            let low = 0;
            let high = rows.length;
            while (low < high) {
                const mid = (low + high) >>> 1;
                if (rows[mid]!.address <= address) low = mid + 1;
                else high = mid;
            }
            const row = rows[low - 1];
            if (row !== undefined) return { file: this.#files[row.file], line: row.line };
        }
        return undefined;
    }
}

/** A DWARF 5 directory or file name table. */
function* readEntries(
    r: Reader,
    sections: LineSections,
    addressSize: number,
): Generator<{ path: string; directory: number }> {
    const formats: [number, number][] = [];
    const formatCount = r.u8();
    for (let i = 0; i < formatCount; i++) formats.push([r.uleb(), r.uleb()]);
    const count = r.uleb();
    for (let i = 0; i < count; i++) {
        let path = "";
        let directory = 0;
        for (const [content, form] of formats) {
            const value = readLineForm(r, form, sections, addressSize);
            if (content === DW_LNCT_path && typeof value === "string") path = value;
            else if (content === DW_LNCT_directory_index && typeof value === "number") directory = value;
        }
        yield { path, directory };
    }
}

function readLineForm(
    r: Reader,
    form: number,
    sections: LineSections,
    addressSize: number,
): string | number | undefined {
    switch (form) {
        case 0x08: // DW_FORM_string
            return r.cstr();
        case 0x0e: // DW_FORM_strp
            return sections.str === undefined ? "" : stringAt(sections.str, r.u32());
        case 0x1f: // DW_FORM_line_strp
            return sections.lineStr === undefined ? "" : stringAt(sections.lineStr, r.u32());
        case 0x0b: // DW_FORM_data1
            return r.u8();
        case 0x05: // DW_FORM_data2
            return r.u16();
        case 0x06: // DW_FORM_data4
            return r.u32();
        case 0x07: // DW_FORM_data8
            return r.u64();
        case 0x0f: // DW_FORM_udata
            return r.uleb();
        case 0x1e: // DW_FORM_data16
            r.skip(16);
            return undefined;
        case 0x09: // DW_FORM_block
            r.skip(r.uleb());
            return undefined;
        case 0x01: // DW_FORM_addr
            return r.sized(addressSize);
    }
    throw new Error(`unsupported form 0x${form.toString(16)} in a line table header`);
}

export function join(directory: string, name: string): string {
    if (name.startsWith("/") || directory === "") return name;
    if (name === "") return directory;
    return directory.endsWith("/") ? directory + name : `${directory}/${name}`;
}
